import { parseInventoryQuantity } from '../../../../../utils/inventoryInputs';
import { cleanManufacturerUrl } from '../../../../../utils/manufacturerUrl';
import {
  convertEuroToUah,
  convertPoundsToUah,
  convertUsdToUah,
  hasPositiveMoney,
  parseMoneyAmount
} from '../../../../../utils/productPrice';
import {
  getPriceCurrencyOption,
  PRICE_CURRENCY_EURO,
  PRICE_CURRENCY_POUNDS,
  PRICE_CURRENCY_UAH,
  PRICE_CURRENCY_USD
} from './constants';

const TEXT_FIELDS = new Set(['name', 'productCode', 'barcode', 'description', 'manufacturerUrl', 'discountCategoryName']);
const RAW_FIELDS = new Set(['quantity', 'discount']);

export const FOREIGN_PRICE_FIELDS = {
  priceInPounds: { rateKey: 'poundRate', convert: convertPoundsToUah },
  priceInUSD: { rateKey: 'dollarRate', convert: convertUsdToUah },
  priceInEuro: { rateKey: 'euroRate', convert: convertEuroToUah }
};

let manualRowSeq = 0;

export const createEmptyManualItem = (categoryIds = [], discountDefaults = {}) => {
  manualRowSeq += 1;
  const discount = discountDefaults.discount == null || discountDefaults.discount === ''
    ? '0'
    : String(discountDefaults.discount);
  const discountCategoryName = discountDefaults.discountCategoryName == null
    ? ''
    : String(discountDefaults.discountCategoryName);
  return {
    key: `manual-${manualRowSeq}`,
    name: '',
    description: '',
    manufacturerUrl: '',
    productCode: '',
    barcode: '',
    quantity: '',
    priceInPounds: '',
    priceInUSD: '',
    priceInEuro: '',
    price: '',
    priceSource: null,
    discount,
    discountCategoryName,
    categoryIds: [...categoryIds],
    selected: true
  };
};

export const createInitialManualItems = (count = 1, categoryIds = [], discountDefaults = {}) =>
  Array.from({ length: Math.max(1, count) }, () => createEmptyManualItem(categoryIds, discountDefaults));

export const getManualFieldDisplay = (item, field) => {
  const value = item[field];
  if (value == null) return '';
  return String(value);
};

/** First line of description for compact table preview. */
export const getDescriptionFirstLine = (raw) => {
  const s = raw == null ? '' : String(raw).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return s.split('\n')[0] || '';
};

/** Replace first line of description; keep remaining lines. */
export const setDescriptionFirstLine = (fullDescription, firstLine) => {
  const rest = String(fullDescription || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').slice(1);
  if (!rest.length) return firstLine == null ? '' : String(firstLine);
  return [firstLine == null ? '' : String(firstLine), ...rest].join('\n');
};

export const isBulkCreateRowValid = (item) =>
  Boolean(String(item?.name || '').trim() && String(item?.productCode || '').trim()) &&
  parseInventoryQuantity(item?.quantity === '' || item?.quantity == null ? 0 : item.quantity, { allowZero: true }) != null;

const clearInactiveCurrencyFields = (item, activeField) => {
  const next = { ...item };
  if (activeField !== 'priceInPounds') next.priceInPounds = '';
  if (activeField !== 'priceInUSD') next.priceInUSD = '';
  if (activeField !== 'priceInEuro') next.priceInEuro = '';
  return next;
};

const uahFromForeignField = (item, field, rates) => {
  const cfg = FOREIGN_PRICE_FIELDS[field];
  if (!cfg) return null;
  const amount = parseMoneyAmount(item[field]);
  const rate = rates[cfg.rateKey];
  if (amount == null || !(rate > 0)) return null;
  return String(cfg.convert(amount, rate));
};

/** Recalculate грн for the active batch currency. Clears stale грн if active foreign amount is empty/invalid. */
export const refreshManualItemUah = (item, rates = {}, priceCurrency = PRICE_CURRENCY_POUNDS) => {
  const opt = getPriceCurrencyOption(priceCurrency);
  if (opt.value === PRICE_CURRENCY_UAH) return item;

  const raw = item[opt.field];
  const trimmed = raw == null ? '' : String(raw).trim();
  if (!trimmed) {
    if (item.price === '' || item.price == null) return item;
    return { ...item, price: '', priceSource: opt.field };
  }

  const uah = uahFromForeignField(item, opt.field, rates);
  if (uah == null) {
    // Amount present but not convertible (bad parse or missing rate) — drop stale грн
    if (item.price === '' || item.price == null) return item;
    return { ...item, price: '', priceSource: opt.field };
  }
  if (String(item.price) === uah && item.priceSource === opt.field) return item;
  return { ...item, price: uah, priceSource: opt.field };
};

export const refreshManualItemsUah = (items, rates, priceCurrency) =>
  (items || []).map(it => refreshManualItemUah(it, rates, priceCurrency));

/**
 * Apply value for a table field. For priceAmount, uses priceCurrency to pick £/$/€/грн.
 */
export const applyManualFieldValue = (item, field, rawValue, rates = {}, priceCurrency = PRICE_CURRENCY_POUNDS) => {
  const asText = rawValue == null ? '' : String(rawValue);

  if (field === 'priceAmount') {
    const opt = getPriceCurrencyOption(priceCurrency);
    let next = clearInactiveCurrencyFields(item, opt.field);
    next[opt.field] = asText;

    if (opt.value === PRICE_CURRENCY_UAH) {
      next.priceSource = 'uah';
      return next;
    }

    next.priceSource = opt.field;
    const amount = parseMoneyAmount(asText);
    const rate = rates[opt.rateKey];
    if (amount != null && rate > 0) {
      next.price = String(FOREIGN_PRICE_FIELDS[opt.field].convert(amount, rate));
    } else {
      // Empty or invalid foreign amount must not keep previous грн
      next.price = '';
    }
    return next;
  }

  const next = { ...item };

  if (TEXT_FIELDS.has(field) || RAW_FIELDS.has(field)) {
    next[field] = field === 'manufacturerUrl' ? cleanManufacturerUrl(asText) : asText;
    return next;
  }

  // Legacy direct currency field edits (tests / paste mapped to priceAmount)
  const foreign = FOREIGN_PRICE_FIELDS[field];
  if (foreign) {
    return applyManualFieldValue(item, 'priceAmount', asText, rates, (
      field === 'priceInPounds' ? PRICE_CURRENCY_POUNDS
        : field === 'priceInUSD' ? PRICE_CURRENCY_USD
          : PRICE_CURRENCY_EURO
    ));
  }

  if (field === 'price') {
    return applyManualFieldValue(item, 'priceAmount', asText, rates, PRICE_CURRENCY_UAH);
  }

  return next;
};

/**
 * Paste multi-line clipboard into one column starting at startIndex.
 * Each line fills the next row (no hard row limit).
 */
export const applyColumnPaste = (items, startIndex, field, text, rates = {}, categoryIds = [], priceCurrency = PRICE_CURRENCY_POUNDS, discountDefaults = {}) => {
  const normalized = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u2028|\u2029/g, '\n');
  const lines = normalized.split('\n');
  if (!lines.length) {
    return { items, added: 0 };
  }

  const next = items.map(it => ({ ...it }));
  let added = 0;

  for (let i = 0; i < lines.length; i++) {
    const rowIndex = startIndex + i;
    if (rowIndex >= next.length) {
      next.push(createEmptyManualItem(categoryIds, discountDefaults));
      added += 1;
    }
    const lineValue = TEXT_FIELDS.has(field) ? lines[i].trim() : lines[i];
    next[rowIndex] = applyManualFieldValue(next[rowIndex], field, lineValue, rates, priceCurrency);
  }

  return { items: next, added };
};

export const getPriceAmountField = (priceCurrency) => getPriceCurrencyOption(priceCurrency).field;

export const itemNeedsActiveCurrencyRate = (item, priceCurrency) => {
  const opt = getPriceCurrencyOption(priceCurrency);
  if (!opt.rateKey) return false;
  return hasPositiveMoney(item?.[opt.field]);
};

/** Drop inactive currency amounts before API submit; clear грн if foreign amount empty. */
export const normalizeItemForPriceCurrency = (item, priceCurrency) => {
  const opt = getPriceCurrencyOption(priceCurrency);
  let next = clearInactiveCurrencyFields(item, opt.field);
  if (opt.value === PRICE_CURRENCY_UAH) {
    next.priceInPounds = '';
    next.priceInUSD = '';
    next.priceInEuro = '';
    return next;
  }
  const trimmed = next[opt.field] == null ? '' : String(next[opt.field]).trim();
  if (!trimmed || parseMoneyAmount(trimmed) == null) {
    next = { ...next, price: '' };
  }
  return next;
};

export const applyDiscountGlobalsToItems = (items, discount, discountCategoryName) => {
  const nextDiscount = discount === '' || discount == null ? '0' : String(discount);
  const nextCategory = discountCategoryName == null ? '' : String(discountCategoryName);
  let changed = false;
  const next = (items || []).map(it => {
    if (String(it.discount ?? '') === nextDiscount && String(it.discountCategoryName ?? '') === nextCategory) {
      return it;
    }
    changed = true;
    return { ...it, discount: nextDiscount, discountCategoryName: nextCategory };
  });
  return changed ? next : items;
};

export const isManualRowEmpty = (item) =>
  !String(item.name || '').trim()
  && !String(item.description || '').trim()
  && !String(item.manufacturerUrl || '').trim()
  && !String(item.productCode || '').trim()
  && !String(item.barcode || '').trim()
  && !String(item.quantity || '').trim()
  && !String(item.priceInPounds || '').trim()
  && !String(item.priceInUSD || '').trim()
  && !String(item.priceInEuro || '').trim()
  && !String(item.price || '').trim();
